import * as React from 'react';
import {
    Button,
    Box,
    Typography,
    TextField,
    Popover
} from '@mui/material';

type NewsEditorModalLinkButtonProps = {
    contentEditableRef: React.RefObject<HTMLDivElement>;
    onLinkInserted: () => void;
    labels: {
        insertLinkTitle: string;
        insertLinkTextPlaceholder: string;
        insertLinkUrlPlaceholder: string;
        insertLinkButton: string;
    };
};

export default function NewsEditorModalLinkButton({
    contentEditableRef,
    onLinkInserted,
    labels
}: NewsEditorModalLinkButtonProps) {
    const savedContentSelectionRef = React.useRef<Range | null>(null);
    const [popoverAnchorElement, setPopoverAnchorElement] = React.useState<HTMLElement | null>(null);
    const [linkText, setLinkText] = React.useState('');
    const [linkUrl, setLinkUrl] = React.useState('');

    const isPopoverOpen = Boolean(popoverAnchorElement);

    const handleIconClick = (event: React.MouseEvent<HTMLElement>) => {
        const contentArea = contentEditableRef.current;
        const selection = window.getSelection();
        const range = contentArea && selection && selection.rangeCount > 0
            ? selection.getRangeAt(0)
            : null;
        const isInsideContent = range && contentArea && contentArea.contains(range.commonAncestorContainer);

        savedContentSelectionRef.current = isInsideContent ? range.cloneRange() : null;
        setLinkText(isInsideContent ? selection.toString() : '');
        setLinkUrl('');
        setPopoverAnchorElement(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setPopoverAnchorElement(null);
    };

    const handleInsertLink = () => {
        const contentArea = contentEditableRef.current;
        if (!contentArea) {
            return;
        }

        try {
            const parsedUrl = new URL(linkUrl);
            if (parsedUrl.protocol !== 'https:' && parsedUrl.protocol !== 'http:') {
                return;
            }
        } catch (e) {
            return;
        }

        const linkElement = document.createElement('a');
        linkElement.href = linkUrl;
        linkElement.textContent = linkText;
        linkElement.style.color = '#1976d2';
        linkElement.style.textDecoration = 'underline';

        contentArea.focus();

        const selection = window.getSelection();
        const savedSelection = savedContentSelectionRef.current;

        if (selection && savedSelection) {
            selection.removeAllRanges();
            selection.addRange(savedSelection);
            if (!selection.isCollapsed) {
                selection.deleteFromDocument();
            }
            const range = selection.getRangeAt(0);
            range.insertNode(linkElement);
            range.setStartAfter(linkElement);
            range.setEndAfter(linkElement);
            selection.removeAllRanges();
            selection.addRange(range);
        } else if (selection) {
            contentArea.appendChild(linkElement);
        }

        onLinkInserted();
        setPopoverAnchorElement(null);
    };

    return (
        <>
            <Box
                onClick={handleIconClick}
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                >
                    <path
                        d="M3.9 12C3.9 10.29 5.29 8.9 7 8.9H11V7H7C4.24 7 2 9.24 2 12C2 14.76 4.24 17 7 17H11V15.1H7C5.29 15.1 3.9 13.71 3.9 12ZM8 13H16V11H8V13ZM17 7H13V8.9H17C18.71 8.9 20.1 10.29 20.1 12C20.1 13.71 18.71 15.1 17 15.1H13V17H17C19.76 17 22 14.76 22 12C22 9.24 19.76 7 17 7Z"
                        fill="black"
                        fillOpacity="0.54"
                    />
                </svg>
            </Box>
            <Popover
                open={isPopoverOpen}
                anchorEl={popoverAnchorElement}
                onClose={handlePopoverClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
            >
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, width: 320 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {labels.insertLinkTitle}
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={labels.insertLinkTextPlaceholder}
                        value={linkText}
                        onChange={(e) => setLinkText(e.target.value)}
                    />
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={labels.insertLinkUrlPlaceholder}
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                    />
                    <Box display="flex" justifyContent="flex-end">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleInsertLink}
                            disabled={!linkText || !linkUrl}
                            sx={{ textTransform: 'none', borderRadius: 0 }}
                        >
                            {labels.insertLinkButton}
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </>
    );
}
